/**
 * Resources — Dispatch Register (native).
 * One owner for registering people / ground / air and reading status.
 * Response owns Assign / Queue / drag-send / robots command tool. Soft≠Live.
 * Soft≠Live · Live SoR — PATCH profile + crew; Crew = Live.
 */
import { useEffect, useMemo, useState } from "react";
import { html, Ico, Alert, Field, Input, KV, stay, go } from "./ui.js";
import { path, useApi, useAction } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { personnelRosterOf, personnelTrackQueryOf, softResponseErrorOf } from "./response-live.js";
import { softExternalFleetErrorOf } from "./external-fleet-desk.js";
import {
  applyPeopleRegisterOverlay,
  peopleRegisterFieldsOf,
  peopleRegisterSubscribe,
  savePeopleRegister,
} from "./resources-people-registry.js";

const KINDS = [
  {
    id: "people",
    label: "People",
    stamp: "PPL",
    accent: "mint",
    blurb: "Names on this site",
    say: "Live profile + crew. Response sends.",
  },
  {
    id: "robots",
    label: "Robots",
    stamp: "GRD",
    accent: "steel",
    blurb: "Ground units · serial",
    say: "Register ground units. Response sends.",
  },
  {
    id: "drones",
    label: "Drones",
    stamp: "AIR",
    accent: "sand",
    blurb: "Airport and aircraft",
    say: "Register airports and aircraft. Response sends.",
  },
];

/* DJI Cloud API domain 3 = dock. Dock 3 + dock aircraft keys from Live catalog. */
const DJI_DOCK_KEYS = new Set(["dji.dock", "dji.dock_2", "dji.dock_3"]);
const DJI_DOCK3_AIR = new Set(["dji.m4d", "dji.m4td"]);
const DJI_DOCK2_AIR = new Set(["dji.m3d", "dji.m3td"]);

function catalogCategory(m) {
  return String((m && m.category) || "").toLowerCase();
}

function isDockModel(m) {
  if (!m) return false;
  const key = String(m.model_key || "").trim();
  return catalogCategory(m) === "dock" || DJI_DOCK_KEYS.has(key);
}

function isAircraftModel(m) {
  if (!m) return false;
  const cat = catalogCategory(m);
  if (cat === "dock" || cat === "payload" || cat === "remote") return false;
  return cat === "aircraft" || !cat;
}

function unitIsDock(u) {
  const key = String((u && u.model_key) || "").trim();
  return DJI_DOCK_KEYS.has(key) || /dock/i.test(key);
}

function modelLabel(m) {
  return (m && (m.display_name || m.model_key)) || "—";
}

function unitreeFamilyOf(m) {
  return String((m && m.family) || "other").toLowerCase();
}

function safePath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    if (op === "get__personnel") {
      const lim = (opts && opts.query && opts.query.limit) || 200;
      return `/personnel?limit=${encodeURIComponent(lim)}`;
    }
    if (op === "get__personnel_policy") return "/personnel/policy";
    if (op === "get__ops_person_status") return "/ops/person/status";
    if (op === "get__personnel_p_track") {
      const u = opts && opts.pathParams && opts.pathParams[0];
      const q = (opts && opts.query) || {};
      if (!u) return "";
      const parts = new URLSearchParams();
      if (q.limit != null && String(q.limit) !== "") parts.set("limit", String(q.limit));
      if (q.since) parts.set("since", String(q.since));
      if (q.until) parts.set("until", String(q.until));
      const qs = parts.toString();
      return `/personnel/${encodeURIComponent(u)}/track${qs ? `?${qs}` : ""}`;
    }
    if (op === "patch__personnel_p_profile") {
      const u = opts && opts.pathParams && opts.pathParams[0];
      return u ? `/personnel/${encodeURIComponent(u)}/profile` : "";
    }
    if (op === "patch__personnel_p_crew") {
      const u = opts && opts.pathParams && opts.pathParams[0];
      return u ? `/personnel/${encodeURIComponent(u)}/crew` : "";
    }
    if (op === "get__external-robots_status") return "/external-robots/status";
    if (op === "get__external-robots_units") return "/external-robots/units";
    if (op === "get__external-robots_bridges") return "/external-robots/bridges";
    if (op === "get__external-robots_integrations") return "/external-robots/integrations";
    if (op === "get__external-robots_catalog") {
      const v = (opts && opts.query && opts.query.vendor) || "dji";
      return `/external-robots/catalog?vendor=${encodeURIComponent(v)}`;
    }
    if (op === "get__external-robots_bridge-adapters") {
      const v = (opts && opts.query && opts.query.vendor) || "dji";
      return `/external-robots/bridge-adapters?vendor=${encodeURIComponent(v)}`;
    }
    if (op === "post__external-robots_integrations") return "/external-robots/integrations";
    if (op === "post__external-robots_units") return "/external-robots/units";
    if (op === "post__external-robots_bridges") return "/external-robots/bridges";
    return "";
  }
}

function personTone(row) {
  if (!row) return "off";
  if (row.assignmentSay && row.assignmentSay !== "Not on a Live job") return "busy";
  if (row.on && row.status === "active") return "ready";
  if (row.status === "active") return "standby";
  return "off";
}

function personState(row) {
  const t = personTone(row);
  if (t === "busy") return "On a run";
  if (t === "ready") return "Ready";
  if (t === "standby") return "Needs on-call";
  if (row && row.status === "revoked") return "Revoked";
  return "Needs consent";
}

function unitVendor(u) {
  return String((u && u.vendor) || "").toLowerCase();
}

function unitOnline(u, bridgesById) {
  if (!u || typeof u !== "object") return null;
  if (u.online === true || u.online === false) return u.online;
  const bid = String(u.bridge_id || u.seb_bridge_id || "").trim();
  if (bid && bridgesById[bid] && typeof bridgesById[bid].online === "boolean") {
    return bridgesById[bid].online;
  }
  const st = String(u.status || "").toLowerCase();
  if (/(online|ready|active|hb_ok)/.test(st)) return true;
  if (/(offline|stale|down|error)/.test(st)) return false;
  return null;
}

function unitTone(online) {
  if (online === true) return "ready";
  if (online === false) return "off";
  return "standby";
}

function unitState(online) {
  if (online === true) return "Online";
  if (online === false) return "Offline";
  return "Awaiting HB";
}

function Chip({ tone, children }) {
  return html`<span className="mux__chip" data-tone=${tone}>${children}</span>`;
}

function Metric({ label, value }) {
  return html`<div className="mux__metric">
    <span>${label}</span>
    <b>${value}</b>
  </div>`;
}

function PeoplePane({ toast, q, filter, selectedId, onSelect }) {
  const peopleApi = useApi(safePath("get__personnel", { query: { limit: 200 } }));
  const policyApi = useApi(safePath("get__personnel_policy") || "/personnel/policy");
  const personPosture = useApi(safePath("get__ops_person_status") || "/ops/person/status");
  const registerWrite = personPosture.loading ? false : (
    !!(personPosture.data && personPosture.data.register_write_allowed === true)
  );
  const [tick, setTick] = useState(0);
  useEffect(() => peopleRegisterSubscribe(() => setTick((n) => n + 1)), []);

  const rows = useMemo(() => {
    const base = personnelRosterOf(peopleApi.data);
    return applyPeopleRegisterOverlay(base, { soft: false });
  }, [peopleApi.data, tick]);

  const shown = useMemo(() => {
    const needle = String(q || "").trim().toLowerCase();
    return rows.filter((r) => {
      if (filter === "ready" && personTone(r) !== "ready") return false;
      if (filter === "busy" && personTone(r) !== "busy") return false;
      if (filter === "off" && !["off", "standby"].includes(personTone(r))) return false;
      if (!needle) return true;
      return `${r.name} ${r.user} ${r.phone} ${r.callsign || ""} ${r.crew || ""} ${r.notes || ""}`.toLowerCase().includes(needle);
    });
  }, [rows, q, filter]);
  const selected = rows.find((r) => r.user === selectedId) || shown[0] || null;
  useEffect(() => {
    if (selected && selected.user !== selectedId) onSelect(selected.user);
  }, [selected && selected.user]);

  const policy = policyApi.data && typeof policyApi.data === "object" ? policyApi.data : null;
  const trackArmed = policy ? policy.track_available === true : null;
  const [trackSince, setTrackSince] = useState("");
  const [trackUntil, setTrackUntil] = useState("");
  const trackQuery = useMemo(
    () => personnelTrackQueryOf({ limit: 8, since: trackSince, until: trackUntil }),
    [trackSince, trackUntil],
  );
  const trackPath = selected && trackArmed === true
    ? (safePath("get__personnel_p_track", { pathParams: [selected.user], query: trackQuery })
      || `/personnel/${encodeURIComponent(selected.user)}/track?limit=8`)
    : "";
  const trackApi = useApi(trackPath);

  const [draft, setDraft] = useState(() => peopleRegisterFieldsOf(selected));
  useEffect(() => {
    setDraft(peopleRegisterFieldsOf(selected));
    setTrackSince("");
    setTrackUntil("");
  }, [selected && selected.user, tick, peopleApi.data]);
  const crewSave = useAction();
  const profileSave = useAction();

  const setField = (key) => (e) => {
    const value = e && e.target ? e.target.value : "";
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const save = async () => {
    if (!registerWrite) {
      toast && toast("Register write is closed for this seat", "warn");
      return;
    }
    if (!selected) return;
    savePeopleRegister(selected.user, draft, { soft: false });
    const profileBody = {
      display_name: String(draft.name || "").trim(),
      callsign: String(draft.callsign || "").trim(),
      role_label: String(draft.role || "").trim(),
      site_label: String(draft.site || "").trim(),
      notes: String(draft.notes || "").trim(),
      phone: String(draft.phone || "").trim(),
      phone_region: String(draft.phoneRegion || "").trim(),
    };
    const profilePath = safePath("patch__personnel_p_profile", { pathParams: [selected.user] })
      || `/personnel/${encodeURIComponent(selected.user)}/profile`;
    const crewPath = safePath("patch__personnel_p_crew", { pathParams: [selected.user] })
      || `/personnel/${encodeURIComponent(selected.user)}/crew`;
    const profileOk = await profileSave.run(
      profilePath,
      { method: "PATCH", body: profileBody },
    );
    const crewOk = await crewSave.run(
      crewPath,
      { method: "PATCH", body: { crew: String(draft.crew || "").trim() } },
    );
    setTick((n) => n + 1);
    if (peopleApi.reload) peopleApi.reload();
    if (!profileOk && !crewOk) {
      toast && toast(
        softResponseErrorOf(profileSave.error || crewSave.error || "Live register write failed"),
        "bad",
      );
      return;
    }
    if (!profileOk) {
      toast && toast(
        softResponseErrorOf(profileSave.error || "Crew saved. Profile write failed."),
        "warn",
      );
      return;
    }
    if (!crewOk) {
      toast && toast(
        softResponseErrorOf(crewSave.error || "Profile saved. Crew write failed."),
        "warn",
      );
      return;
    }
    toast && toast("Live register saved · profile + crew", "go");
  };

  const ready = rows.filter((r) => personTone(r) === "ready").length;
  const busyN = rows.filter((r) => personTone(r) === "busy").length;
  const offN = rows.length - ready - busyN;

  const trackItems = ((trackApi.data && (trackApi.data.items || trackApi.data.points)) || []);
  const trackN = Array.isArray(trackItems) ? trackItems.length : 0;
  const trackSay = trackArmed === false
    ? "History dark · not armed"
    : (trackArmed == null && policyApi.loading
      ? "…"
      : (trackApi.loading ? "…" : (trackApi.error ? softResponseErrorOf(trackApi.error) : String(trackN))));

  return html`<div className="mux__pane" data-people-job="register-edit" data-res-gap="policy-track">
    <div className="mux__strip" aria-label="People register counts">
      <${Metric} label="Listed" value=${rows.length}/>
      <${Metric} label="Ready" value=${ready}/>
      <${Metric} label="Busy" value=${busyN}/>
      <${Metric} label="Not ready" value=${offN}/>
    </div>
    ${policy
      ? html`<div className="mux__policy" data-res-gap="personnel-policy" aria-label="Live personnel policy">
          <b>Live policy</b>
          <span>Stale ≥ ${policy.stale_threshold_minutes != null ? policy.stale_threshold_minutes : "—"} min</span>
          <span>Fresh-only · ${policy.fresh_only_dispatch === true ? "on" : "off"}</span>
          <span data-track=${policy.track_available === true ? "1" : "0"}>Track · ${policy.track_available === true ? "armed" : "dark"}</span>
          <em>${policy.source || "deployment_env"}</em>
        </div>`
      : (policyApi.error
        ? html`<${Alert} tone="warn" title="Policy unread" say=${softResponseErrorOf(policyApi.error)}/>`
        : null)}
    <div className="mux__split">
      <section className="mux__list" aria-label="People register list">
        ${peopleApi.error
          ? html`<${Alert} tone="warn" title="Could not load people" say=${softResponseErrorOf(peopleApi.error)}/>`
          : null}
        ${peopleApi.loading && !rows.length
          ? html`<p className="mux__empty">Loading members…</p>`
          : !shown.length
            ? html`<div className="mux__empty" data-res-gap="membership-cta">
                <b>No members on this seat</b>
                <p>People appear after site membership and consent on the handset.</p>
                <button type="button" className="btn btn--ghost btn--sm"
                  onClick=${() => go("/places/sites")}>
                  <${Ico} d="map" w=${13}/> Open work charter · membership
                </button>
              </div>`
            : shown.map((r) => html`
                <button key=${r.user} type="button" className="mux__row"
                  data-on=${selected && selected.user === r.user ? "1" : "0"}
                  data-tone=${personTone(r)}
                  onClick=${() => onSelect(r.user)}>
                  <i aria-hidden="true"/>
                  <span>
                    <b>${r.name}${r.registerProfileLive ? html` <em className="mux__soft-tag" title="Live register profile">Live</em>` : null}</b>
                    <em>${r.crew ? r.crew + " · " : ""}${r.callsign ? r.callsign + " · " : ""}${r.phoneSay || r.user}</em>
                  </span>
                  <${Chip} tone=${personTone(r)}>${personState(r)}<//>
                </button>`)}
      </section>
      <aside className="mux__panel" aria-label="Edit people register">
        ${!selected
          ? html`<div className="mux__empty">
              <b>Select a member</b>
              <p>Edit Live register labels here. Response owns Assign and drag-send — no send controls on this page.</p>
            </div>`
          : html`<div className="mux__panel-body">
              <p className="mux__panel-kicker">Register edit · Live</p>
              <h2>${draft.name || selected.user}</h2>
              <p className="mux__panel-sub">${selected.user} · profile + crew · Response still sends</p>
              <${Alert} tone="warn" title="Live register"
                say="Save writes Live profile + Crew. Site label is a register note — not Places membership."/>
              <div className="mux__form mux__form--register">
                <${Field} label="Display name">
                  <${Input} icon="user" value=${draft.name} onChange=${setField("name")}/>
                <//>
                <${Field} label="Callsign">
                  <${Input} icon="radio" value=${draft.callsign} onChange=${setField("callsign")}/>
                <//>
                <${Field} label="Crew" hint="Live people · not Machine Group">
                  <${Input} icon="users" value=${draft.crew} onChange=${setField("crew")} placeholder="e.g. NightWatch"/>
                <//>
                <${Field} label="Role">
                  <${Input} icon="shield" value=${draft.role} onChange=${setField("role")}/>
                <//>
                <${Field} label="Site label" hint="Live register note · not fabric membership">
                  <${Input} icon="map" value=${draft.site} onChange=${setField("site")}/>
                <//>
                <${Field} label="Phone">
                  <${Input} icon="phone" value=${draft.phone} onChange=${setField("phone")}/>
                <//>
                <${Field} label="Phone region">
                  <${Input} icon="book" value=${draft.phoneRegion} onChange=${setField("phoneRegion")}/>
                <//>
                <${Field} label="Notes">
                  <${Input} icon="book" value=${draft.notes} onChange=${setField("notes")}/>
                <//>
              </div>
              <div className="mux__fields mux__fields--readonly">
                ${[
                  ["Consent", selected.status === "active" ? "Granted" : selected.status],
                  ["On call", selected.on ? "Yes" : "No"],
                  ["Live fix", selected.positionSay],
                  ["Assignment", selected.assignmentSay],
                  ["Track points", trackSay],
                ].map(([k, v]) => html`
                  <div key=${k} className="mux__field"><u>${k}</u><b>${v || "—"}</b></div>`)}
              </div>
              ${trackArmed === true ? html`
                <div className="mux__form mux__form--track-range" data-res-gap="track-range">
                  <${Field} label="Track since (local)">
                    <input type="datetime-local" className="input" value=${trackSince}
                      onInput=${(e) => setTrackSince(e.target.value)}/>
                  <//>
                  <${Field} label="Track until (local)">
                    <input type="datetime-local" className="input" value=${trackUntil}
                      onInput=${(e) => setTrackUntil(e.target.value)}/>
                  <//>
                  <button type="button" className="btn btn--ghost btn--sm"
                    onClick=${() => { setTrackSince(""); setTrackUntil(""); }}>
                    Clear track window
                  </button>
                </div>` : null}
              <p className="mux__hint">
                Membership is on Places charter. Track detail opens on Ops map person sheet. Assign stays in Response.
              </p>
              <div className="mux__panel-acts">
                <button type="button" className="btn btn--go btn--sm"
                  data-cmc-register-save-ok=${registerWrite ? "1" : "0"}
                  disabled=${!registerWrite || profileSave.busy || crewSave.busy}
                  onClick=${save}>
                  <${Ico} d="check" w=${13}/> Save Live register
                </button>
                <button type="button" className="btn btn--ghost btn--sm"
                  onClick=${() => {
                    setDraft(peopleRegisterFieldsOf(selected));
                    peopleApi.reload && peopleApi.reload();
                    toast && toast("Draft reset", "go");
                  }}>
                  <${Ico} d="refresh" w=${13}/> Reset
                </button>
                <button type="button" className="btn btn--ghost btn--sm"
                  onClick=${() => go("/ops")}>
                  <${Ico} d="map" w=${13}/> Ops · person track
                </button>
                ${draft.phone
                  ? html`<a className="btn btn--ghost btn--sm" href=${"tel:" + String(draft.phone).replace(/\s+/g, "")}>Call</a>`
                  : null}
              </div>
            </div>`}
      </aside>
    </div>
  </div>`;
}

function MachinePane({ kind, toast, q, filter, selectedId, onSelect }) {
  const isAir = kind === "drones";
  const vendor = isAir ? "dji" : "unitree";
  const vendorLabel = isAir ? "DJI" : "Unitree";
  const posture = useApi(safePath("get__external-robots_status"));
  const armed = posture.data && posture.data.enabled === true;
  const writeOk = posture.data && posture.data.write_allowed === true && armed;
  const unitsApi = useApi(armed ? safePath("get__external-robots_units") : "");
  const bridgesApi = useApi(armed ? safePath("get__external-robots_bridges") : "");
  const integrationsApi = useApi(armed ? safePath("get__external-robots_integrations") : "");
  const catalogApi = useApi(armed ? safePath("get__external-robots_catalog", { query: { vendor } }) : "");
  const adaptersApi = useApi(armed ? safePath("get__external-robots_bridge-adapters", { query: { vendor } }) : "");
  const { run, busy, error, envelope } = useAction();

  /* Airport vs aircraft mirrors DJI Cloud API / Dock 3 onboarding (FlytBase · DroneDeploy). */
  const [assetClass, setAssetClass] = useState(isAir ? "airport" : "unit");
  const [family, setFamily] = useState("all");
  const [displayName, setDisplayName] = useState("");
  const [integrationId, setIntegrationId] = useState("");
  const [modelKey, setModelKey] = useState(isAir ? "dji.dock_3" : "");
  const [serial, setSerial] = useState("");
  const [homeAirportId, setHomeAirportId] = useState("");
  const [bridgeName, setBridgeName] = useState("");
  const [adapterKind, setAdapterKind] = useState("");

  useEffect(() => {
    setAssetClass(isAir ? "airport" : "unit");
    setFamily("all");
    setModelKey(isAir ? "dji.dock_3" : "");
    setSerial("");
    setHomeAirportId("");
    setBridgeName("");
    setAdapterKind("");
  }, [kind]);

  useEffect(() => {
    if (!isAir) return;
    if (assetClass === "airport") setModelKey((k) => (DJI_DOCK_KEYS.has(k) ? k : "dji.dock_3"));
    else setModelKey((k) => (DJI_DOCK_KEYS.has(k) ? "dji.m4d" : k || "dji.m4d"));
  }, [assetClass, isAir]);

  const bridgesById = useMemo(() => {
    const items = (bridgesApi.data && (bridgesApi.data.items || bridgesApi.data.bridges)) || [];
    const map = Object.create(null);
    if (Array.isArray(items)) {
      for (const b of items) {
        const id = String((b && b.bridge_id) || "").trim();
        if (id) map[id] = b;
      }
    }
    return map;
  }, [bridgesApi.data]);

  const models = useMemo(() => {
    const list = (catalogApi.data && Array.isArray(catalogApi.data.models)) ? catalogApi.data.models : [];
    return list;
  }, [catalogApi.data]);

  const units = useMemo(() => {
    const items = (unitsApi.data && (unitsApi.data.items || unitsApi.data.units)) || [];
    if (!Array.isArray(items)) return [];
    return items.filter((u) => {
      const v = unitVendor(u);
      if (isAir) return v === "dji" || /dji|air|drone|dock/.test(v);
      return v === "unitree" || /unitree|ground/.test(v);
    });
  }, [unitsApi.data, isAir]);

  const airports = useMemo(() => units.filter((u) => unitIsDock(u)), [units]);
  const aircraft = useMemo(() => units.filter((u) => !unitIsDock(u)), [units]);

  const classUnits = useMemo(() => {
    if (!isAir) return units;
    return assetClass === "airport" ? airports : aircraft;
  }, [isAir, assetClass, units, airports, aircraft]);

  const shown = useMemo(() => {
    const needle = String(q || "").trim().toLowerCase();
    return classUnits.filter((u) => {
      const online = unitOnline(u, bridgesById);
      const tone = unitTone(online);
      if (filter === "ready" && tone !== "ready") return false;
      if (filter === "off" && tone !== "off") return false;
      if (filter === "busy" && tone !== "standby") return false;
      if (!needle) return true;
      return `${u.serial || ""} ${u.unit_id || ""} ${u.model_key || ""} ${u.site_id || ""}`
        .toLowerCase().includes(needle);
    });
  }, [classUnits, q, filter, bridgesById]);

  const selected = units.find((u) => String(u.unit_id || "") === selectedId) || shown[0] || null;
  useEffect(() => {
    const id = selected ? String(selected.unit_id || "") : "";
    if (id && id !== selectedId) onSelect(id);
  }, [selected && selected.unit_id]);

  const onlineN = units.filter((u) => unitOnline(u, bridgesById) === true).length;
  const offlineN = units.filter((u) => unitOnline(u, bridgesById) === false).length;
  const awaitN = units.length - onlineN - offlineN;
  const intItems = ((integrationsApi.data && (integrationsApi.data.items || integrationsApi.data.integrations)) || [])
    .filter((x) => !x.vendor || String(x.vendor).toLowerCase() === vendor);
  const adapters = (adaptersApi.data && adaptersApi.data.items) || [];

  const modelChoices = useMemo(() => {
    if (isAir) {
      const list = models.filter((m) => (assetClass === "airport" ? isDockModel(m) : isAircraftModel(m)));
      /* Prefer Dock 3 / Dock 3 aircraft at the front — industry default for new airports. */
      return list.slice().sort((a, b) => {
        const score = (m) => {
          const k = String(m.model_key || "");
          if (k === "dji.dock_3") return 0;
          if (DJI_DOCK3_AIR.has(k)) return 1;
          if (k === "dji.dock_2") return 2;
          if (DJI_DOCK2_AIR.has(k)) return 3;
          if (isDockModel(m)) return 4;
          return 5;
        };
        return score(a) - score(b);
      });
    }
    return models.filter((m) => family === "all" || unitreeFamilyOf(m) === family);
  }, [models, isAir, assetClass, family]);

  const homeAirportLabel = (dock) => {
    if (!dock) return "—";
    const name = String(dock.serial || dock.unit_id || "");
    const mk = String(dock.model_key || "");
    if (mk === "dji.dock_3") return `${name} · Dock 3`;
    if (mk === "dji.dock_2") return `${name} · Dock 2`;
    return `${name} · ${mk || "Airport"}`;
  };

  const addIntegration = async () => {
    if (!writeOk) { toast("Register sealed on this seat", "warn"); return; }
    if (!displayName.trim()) { toast("Link name required", "warn"); return; }
    const out = await run(safePath("post__external-robots_integrations"), {
      body: {
        vendor,
        integration_kind: isAir ? "dji_cloud_api" : "unitree_edge_bridge",
        display_name: displayName.trim(),
      },
    });
    if (!out) { toast(softExternalFleetErrorOf(error || "Could not create link"), "bad"); return; }
    const iid = String(out.integration_id || "").trim();
    if (iid) setIntegrationId(iid);
    integrationsApi.reload && integrationsApi.reload();
    toast(isAir ? "Cloud link created" : "Bridge link created", "go");
    setDisplayName("");
  };

  const addUnit = async () => {
    if (!writeOk) { toast("Register sealed on this seat", "warn"); return; }
    if (!integrationId || !modelKey || !serial) {
      toast(isAir
        ? "Cloud link, model, and serial number are required"
        : "Link, model, and serial are required", "warn");
      return;
    }
    const body = {
      integration_id: integrationId,
      vendor,
      model_key: modelKey,
      serial: serial.trim(),
    };
    /* site_id = home airport unit_id — allowed pairing field, no Soft invent of bind. */
    if (isAir && assetClass === "aircraft" && homeAirportId) {
      body.site_id = homeAirportId;
    }
    const out = await run(safePath("post__external-robots_units"), { body });
    if (!out) { toast(softExternalFleetErrorOf(error || "Could not register"), "bad"); return; }
    unitsApi.reload && unitsApi.reload();
    toast(isAir && assetClass === "airport" ? "Airport registered" : "Unit registered", "go");
    setSerial("");
  };

  const saveBridge = async () => {
    if (!writeOk) { toast("Register sealed on this seat", "warn"); return; }
    if (!integrationId || !bridgeName || !adapterKind) {
      toast("Link, bridge name, and adapter are required", "warn");
      return;
    }
    const out = await run(safePath("post__external-robots_bridges"), {
      body: {
        integration_id: integrationId,
        display_name: bridgeName,
        adapter_kind: adapterKind,
        vendor,
        config: {},
      },
    });
    if (!out) { toast(softExternalFleetErrorOf(error || "Could not save bridge"), "bad"); return; }
    bridgesApi.reload && bridgesApi.reload();
    toast(isAir
      ? "MQTT bridge saved — online after dock heartbeat"
      : "Edge bridge saved — online after heartbeat", "go");
  };

  const families = [
    { id: "all", label: "All" },
    { id: "go2", label: "Go2" },
    { id: "b2", label: "B2" },
    { id: "g1", label: "G1" },
    { id: "h1", label: "H1" },
    { id: "legacy", label: "Legacy" },
  ];

  return html`<div className="mux__pane">
    <div className="mux__strip" aria-label=${kind + " status"}>
      ${isAir
        ? html`
            <${Metric} label="Airports" value=${airports.length}/>
            <${Metric} label="Aircraft" value=${aircraft.length}/>
            <${Metric} label="Online" value=${onlineN}/>
            <${Metric} label="Awaiting" value=${awaitN}/>`
        : html`
            <${Metric} label="Listed" value=${units.length}/>
            <${Metric} label="Online" value=${onlineN}/>
            <${Metric} label="Offline" value=${offlineN}/>
            <${Metric} label="Awaiting" value=${awaitN}/>`}
    </div>
    ${!posture.loading && !armed
      ? html`<${Alert} tone="warn" title="Register sealed"
          say="External fleet is not armed. Empty lists are honest — nothing is Soft-filled."/>`
      : null}
    ${envelope ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>` : null}

    ${isAir
      ? html`<div className="mux__class" role="tablist" aria-label="Air register class">
          <button type="button" role="tab" data-on=${assetClass === "airport" ? "1" : "0"}
            aria-selected=${assetClass === "airport" ? "true" : "false"}
            onClick=${() => setAssetClass("airport")}>
            <b>Airport</b><span>Dock · Dock 2 · Dock 3</span>
          </button>
          <button type="button" role="tab" data-on=${assetClass === "aircraft" ? "1" : "0"}
            aria-selected=${assetClass === "aircraft" ? "true" : "false"}
            onClick=${() => setAssetClass("aircraft")}>
            <b>Aircraft</b><span>Air SN · home nest</span>
          </button>
        </div>`
      : html`<div className="mux__class mux__class--families" role="tablist" aria-label="Unitree family">
          ${families.map((f) => html`
            <button key=${f.id} type="button" role="tab"
              data-on=${family === f.id ? "1" : "0"}
              aria-selected=${family === f.id ? "true" : "false"}
              onClick=${() => setFamily(f.id)}>${f.label}</button>`)}
        </div>`}

    <div className="mux__split mux__split--machine">
      <section className="mux__list" aria-label=${kind + " register list"}>
        ${unitsApi.error
          ? html`<${Alert} tone="warn" title="Could not load units" say=${softExternalFleetErrorOf(unitsApi.error)}/>`
          : null}
        ${!shown.length
          ? html`<div className="mux__empty">
              <b>${isAir
                ? (assetClass === "airport" ? "No airports registered" : "No aircraft registered")
                : "No robots registered"}</b>
              <p>${isAir
                ? (assetClass === "airport"
                  ? "Register Dock 3 (or Dock / Dock 2) by SN on the right. Pilot binds Third-party Cloud MQTT on site."
                  : "Register air SN, optionally nest under a Dock 3. Mission send stays in Response.")
                : "Pick a Unitree model and serial on the right, then save an edge bridge for heartbeat."}</p>
            </div>`
          : shown.map((u) => {
              const id = String(u.unit_id || "");
              const online = unitOnline(u, bridgesById);
              const dock = unitIsDock(u);
              const home = u.site_id
                ? airports.find((a) => String(a.unit_id) === String(u.site_id))
                : null;
              return html`<button key=${id} type="button" className="mux__row"
                data-on=${selected && String(selected.unit_id) === id ? "1" : "0"}
                data-tone=${unitTone(online)}
                data-kind=${dock ? "airport" : "air"}
                onClick=${() => onSelect(id)}>
                <i aria-hidden="true"/>
                <span>
                  <b>${u.serial || id}${dock && String(u.model_key) === "dji.dock_3"
                    ? html` <em className="mux__soft-tag">Dock 3</em>` : null}</b>
                  <em>${modelLabel(models.find((m) => m.model_key === u.model_key) || { model_key: u.model_key })
                   }${home ? ` · nest ${home.serial || home.unit_id}` : ""}</em>
                </span>
                <${Chip} tone=${unitTone(online)}>${unitState(online)}<//>
              </button>`;
            })}
      </section>

      <aside className="mux__panel" aria-label=${kind + " register form"}>
        <p className="mux__panel-kicker">${vendorLabel} register</p>
        <h2>${isAir
          ? (assetClass === "airport" ? "Airport · Dock 3" : "Aircraft · nest")
          : "Ground · edge"}</h2>
        <p className="mux__panel-sub">${isAir
          ? "Cloud link → SN → Pilot MQTT on site → heartbeat."
          : "Link → model + serial → edge heartbeat."}</p>

        <ol className="mux__steps" aria-hidden="true">
          <li data-on="1">${isAir ? "Cloud" : "Link"}</li>
          <li data-on="1">${isAir ? (assetClass === "airport" ? "Dock SN" : "Air SN") : "Serial"}</li>
          <li data-on="1">${isAir ? "MQTT" : "Edge"}</li>
        </ol>

        <div className="mux__form">
          <p className="mux__form-label">1 · ${isAir ? "Cloud API link" : "Vendor link"}</p>
          <div className="mux__pick">
            ${intItems.length
              ? intItems.slice(0, 8).map((x) => html`
                  <button key=${x.integration_id} type="button" className="mux__pick-btn"
                    data-on=${integrationId === String(x.integration_id || "") ? "1" : "0"}
                    onClick=${() => setIntegrationId(String(x.integration_id || ""))}>
                    ${x.display_name || x.integration_id}
                  </button>`)
              : html`<span className="note">No links yet</span>`}
          </div>
          <${Field} label="New link name">
            <${Input} icon="book" value=${displayName} onChange=${(e) => setDisplayName(e.target.value)}
              placeholder=${isAir ? "e.g. Site A Cloud" : "e.g. Yard Unitree"}/>
          <//>
          <button type="button" className="btn btn--ghost btn--sm" disabled=${busy || !writeOk}
            onClick=${addIntegration}>Create link</button>

          <p className="mux__form-label">2 · ${isAir
            ? (assetClass === "airport" ? "Register airport" : "Register aircraft")
            : "Register unit"}</p>

          ${isAir && assetClass === "airport"
            ? html`<button type="button" className="mux__featured"
                data-on=${modelKey === "dji.dock_3" ? "1" : "0"}
                onClick=${() => setModelKey("dji.dock_3")}>
                <span className="mux__featured-stamp">DOCK 3</span>
                <b>DJI Dock 3</b>
                <small>Airport nest · pairs with Matrice 4D / 4TD</small>
              </button>`
            : null}

          <div className="mux__pick">
            ${modelChoices.slice(0, 12).map((m) => html`
              <button key=${m.model_key} type="button" className="mux__pick-btn"
                data-on=${modelKey === String(m.model_key || "") ? "1" : "0"}
                data-hot=${String(m.model_key) === "dji.dock_3" || DJI_DOCK3_AIR.has(String(m.model_key)) ? "1" : "0"}
                onClick=${() => setModelKey(String(m.model_key || ""))}>
                ${m.display_name || m.model_key}
              </button>`)}
            ${!modelChoices.length
              ? html`<span className="note">${armed ? "No models in catalog" : "Catalog sealed"}</span>`
              : null}
          </div>

          ${isAir && assetClass === "aircraft" && airports.length
            ? html`<${Field} label="Home airport (optional)">
                <select className="inp mux__select" value=${homeAirportId}
                  onChange=${(e) => setHomeAirportId(e.target.value)}>
                  <option value="">No nest link</option>
                  ${airports.map((a) => html`
                    <option key=${a.unit_id} value=${a.unit_id}>${homeAirportLabel(a)}</option>`)}
                </select>
              <//>`
            : null}

          <${Field} label=${isAir
            ? (assetClass === "airport" ? "Dock serial (SN)" : "Aircraft serial (SN)")
            : "Robot serial"}>
            <${Input} icon="chip" value=${serial} onChange=${(e) => setSerial(e.target.value.trim())}
              placeholder=${isAir ? "From plate / Pilot device info" : "From Unitree plate"}/>
          <//>
          <button type="button" className="btn btn--go btn--sm" disabled=${busy || !writeOk}
            onClick=${addUnit}>${isAir && assetClass === "airport" ? "Register airport" : "Register unit"}</button>

          <p className="mux__form-label">3 · ${isAir ? "Cloud MQTT bridge" : "Edge bridge"}</p>
          <div className="mux__pick">
            ${adapters.slice(0, 6).map((a) => html`
              <button key=${a.adapter_kind} type="button" className="mux__pick-btn"
                data-on=${adapterKind === a.adapter_kind ? "1" : "0"}
                onClick=${() => setAdapterKind(a.adapter_kind)}>
                ${a.label || a.adapter_kind}
              </button>`)}
          </div>
          <${Field} label="Bridge name">
            <${Input} icon="book" value=${bridgeName} onChange=${(e) => setBridgeName(e.target.value)}
              placeholder=${isAir ? "e.g. Dock MQTT" : "e.g. Yard ROS2"}/>
          <//>
          <button type="button" className="btn btn--ghost btn--sm" disabled=${busy || !writeOk}
            onClick=${saveBridge}>Save bridge</button>
        </div>

        <p className="mux__hint mux__hint--bind">${isAir
          ? "Pilot / Enterprise → Third-party Cloud (MQTT + org / bind). This desk registers SN only. GET /external-robots is not a list API — use /units."
          : "Edge stays on your LAN. Online only after Live heartbeat. Register here · command intents stay in Response."}</p>
        <div className="mux__panel-acts" data-res-gap="command-cta">
          <button type="button" className="btn btn--ghost btn--sm"
            onClick=${() => go("/response/robots")}>
            <${Ico} d="arrowR" w=${13}/> Open Response · robots command
          </button>
        </div>

        ${selected
          ? html`<div className="mux__fields mux__fields--selected">
              <p className="mux__form-label">Selected</p>
              ${[
                ["Serial", selected.serial || selected.unit_id],
                ["Model", selected.model_key],
                ["Class", unitIsDock(selected) ? "Airport" : (isAir ? "Aircraft" : "Ground")],
                ["Home airport", selected.site_id
                  ? (airports.find((a) => String(a.unit_id) === String(selected.site_id)) || {}).serial || selected.site_id
                  : "—"],
                ["Status", unitState(unitOnline(selected, bridgesById))],
              ].filter(([k]) => isAir || k !== "Home airport").map(([k, v]) => html`
                <div key=${k} className="mux__field"><u>${k}</u><b>${v || "—"}</b></div>`)}
            </div>`
          : null}
      </aside>
    </div>
  </div>`;
}

export default function ResourcesRegisterDesk({ sub, toast }) {
  const routeKind = KINDS.some((k) => k.id === sub) ? sub : "people";
  const [kind, setKind] = useState(routeKind);
  useEffect(() => {
    setKind(routeKind);
  }, [routeKind]);
  const active = KINDS.find((k) => k.id === kind) || KINDS[0];
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [selectedId, setSelectedId] = useState("");
  useEffect(() => {
    setQ("");
    setFilter("all");
    setSelectedId("");
  }, [kind]);

  const selectKind = (id) => {
    setKind(id);
    stay("/dispatch/" + id);
  };

  /* Lightweight counts for the muster rail */
  const peopleApi = useApi(safePath("get__personnel", { query: { limit: 200 } }));
  const fleetPosture = useApi(safePath("get__external-robots_status"));
  const unitsApi = useApi(
    fleetPosture.data && fleetPosture.data.enabled === true
      ? safePath("get__external-robots_units")
      : "",
  );
  const peopleN = useMemo(() => {
    const base = personnelRosterOf(peopleApi.data);
    return applyPeopleRegisterOverlay(base, { soft: false }).length;
  }, [peopleApi.data]);
  const units = useMemo(() => {
    const items = (unitsApi.data && (unitsApi.data.items || unitsApi.data.units)) || [];
    return Array.isArray(items) ? items : [];
  }, [unitsApi.data]);
  const robotsN = units.filter((u) => {
    const v = unitVendor(u);
    return v === "unitree" || /unitree|ground/.test(v);
  }).length;
  const dronesN = units.filter((u) => {
    const v = unitVendor(u);
    return v === "dji" || /dji|air|drone/.test(v);
  }).length;
  const counts = { people: peopleN, robots: robotsN, drones: dronesN };

  const filters = kind === "people"
    ? [
        { id: "all", label: "All" },
        { id: "ready", label: "Ready" },
        { id: "busy", label: "Busy" },
        { id: "off", label: "Not ready" },
      ]
    : [
        { id: "all", label: "All" },
        { id: "ready", label: "Online" },
        { id: "off", label: "Offline" },
        { id: "busy", label: "Awaiting" },
      ];

  return html`<div className="sheet desk-craft mux" data-cmc-dispatch-ux="dispatch-v1" data-resources-job="register"
    data-dispatch-register="1" data-kind=${kind}>
    <div className="mux__radar" aria-hidden="true"/>
    <div className="mux__shell">
      <header className="mux__mast">
        <div className="mux__mast-copy">
          <p className="mux__kicker">Resources · register</p>
          <h1>Dispatch register</h1>
          <p>This page registers · Response sends · Ops readiness is a separate seat.</p>
          <div className="mux__mast-acts">
            <button type="button" className="btn btn--ghost btn--sm"
              onClick=${() => go("/ops/resources")}>
              <${Ico} d="gauge" w=${13}/> Ops readiness
            </button>
            ${kind === "robots" || kind === "drones"
              ? html`<button type="button" className="btn btn--ghost btn--sm"
                  onClick=${() => go("/response/robots")}>
                  <${Ico} d="arrowR" w=${13}/> Command in Response
                </button>`
              : html`<button type="button" className="btn btn--ghost btn--sm"
                  onClick=${() => go("/places/sites")}>
                  <${Ico} d="map" w=${13}/> Membership · Places
                </button>`}
          </div>
        </div>
        <nav className="mux__orbit" aria-label="Register kind">
          ${KINDS.map((k) => html`
            <button key=${k.id} type="button" className="mux__kind"
              data-accent=${k.accent}
              data-on=${kind === k.id ? "1" : "0"}
              aria-current=${kind === k.id ? "page" : undefined}
              onClick=${() => selectKind(k.id)}>
              <div className="mux__kind-top">
                <span className="mux__kind-glyph" aria-hidden="true">${k.stamp}</span>
                <span className="mux__kind-count">${counts[k.id] || 0}</span>
              </div>
              <b>${k.label}</b>
              <small>${k.blurb}</small>
            </button>`)}
        </nav>
      </header>

      <div className="mux__stage" key=${kind}>
        <div className="mux__stage-head">
          <div className="mux__stage-title">
            <h2>${active.label}</h2>
            <p>${active.say}</p>
          </div>
          <div className="mux__tools">
            <div className="mux__filters" role="tablist" aria-label="Filter">
              ${filters.map((f) => html`
                <button key=${f.id} type="button" role="tab"
                  aria-selected=${filter === f.id ? "true" : "false"}
                  data-on=${filter === f.id ? "1" : "0"}
                  onClick=${() => setFilter(f.id)}>${f.label}</button>`)}
            </div>
            <input className="inp mux__search" type="search"
              placeholder=${kind === "people" ? "Search name or phone…" : "Search serial or model…"}
              aria-label="Search register"
              value=${q}
              onChange=${(e) => setQ(e.target.value)}/>
          </div>
        </div>

        ${kind === "people"
          ? html`<${PeoplePane} key="people" toast=${toast}
              q=${q} filter=${filter} selectedId=${selectedId} onSelect=${setSelectedId}/>`
          : html`<${MachinePane} key=${kind} kind=${kind} toast=${toast}
              q=${q} filter=${filter} selectedId=${selectedId} onSelect=${setSelectedId}/>`}
      </div>
    </div>
  </div>`;
}
